<script>
import {computed, defineComponent, onBeforeMount, reactive, ref, watch} from "vue";
import {useStore} from "vuex";
import {useRoute, useRouter} from "vue-router";
import {useError} from "@/hooks/useErrors";
import {useNotices} from "@/hooks/notices";

import Button from "primevue/button";
import InputText from "primevue/inputtext";
import Checkbox from "primevue/checkbox";
import Calendar from "primevue/calendar";
import Dropdown from "primevue/dropdown";
import InputMask from "primevue/inputmask";
import Breadcrumb from "@/components/Breadcrumb.vue";
import ButtonSuccess from "@/components/buttons/ButtonSuccess";
import MainCard from "@/components/cards/MainCard";
import Editor from "primevue/editor";
import MultiSelect from "primevue/multiselect";
import Badge from "primevue/badge";

import {useRoles} from "@/hooks/role";
import ConfirmationModal from "@/components/modals/ConfirmationModal.vue";
import {useApiCall} from "@/composables/useApiCall";
import {loadUsers, loadUser} from "@/services/users";
import ProgressSpinner from "primevue/progressspinner";
import {useI18n} from "vue-i18n";

export default defineComponent({
  layout: {name: "AdminLayout"},
  components: {
    ProgressSpinner,
    ConfirmationModal,
    Breadcrumb, Button, InputText,
    ButtonSuccess, MainCard, Checkbox,
    MultiSelect, Editor, Dropdown,
    Calendar, InputMask, Badge
  },
  setup() {
    const {t} = useI18n()
    const errors = useError();
    const route = useRoute();
    const router = useRouter();
    const {notice, formCreateNotice, v$, updateNotice, loadNotice, typeNotices, deleteNotice} = useNotices();
    const {roles, rolesApp} = useRoles();

    const apiRoles = computed(() => [...rolesApp.value, ...roles.value] || [])
    const store = useStore();
    const lang = computed(() => store.getters.getLang);
    const titleField = computed(() => `title${lang.value === 'ru' ? '' : `_${lang.value}`}`);
    const textField = computed(() => `text${lang.value === 'ru' ? '' : `_${lang.value}`}`);
    const breadcrumbs = ref([]);

    const toUpdateNotice = async () => {
      try {
        await updateNotice(
            route.params.id,
            formCreateNotice.value,
        )

      } catch (e) {
        console.error(e);
      }

      await loadNotice(route.params.id);
    };

    const toDeclineNotice = async () => {
      try {
        await deleteNotice(route.params.id);
        await router.push({name: 'notices'});
      } catch (e) {
        console.error(e);
      }
    };

    const noticeTypeForm = reactive({
      name: ''
    });

    const visibleConfirmationModal = computed(() => store.getters.getCreateTypeNoticeModal);
    const changeVisible = () => store.dispatch('changeStateModal', {
      type: 'createTypeNoticeModal',
      bool: !visibleConfirmationModal.value
    });

    const createTypeNotice = async () => {
      await store.dispatch('fetchCreateTypeNotice', noticeTypeForm);
      await store.dispatch('fetchTypeNotices');
      await changeVisible();
    };

    const allRoles = computed(() => {
      return [
        {id: -2, name: 'all', name_ru: 'Все пользователи'},
        ...(apiRoles.value.length ? apiRoles.value : []),
        {id: -1, name: 'user', name_ru: 'Определенный пользователь'},
      ]
    })
    const isOwnerId = computed(() => {
      const owner = apiRoles.value.find(item => item.name === 'owner')
      return owner ? owner.id : null
    })
    const statuses = ref([
      {title: 'Все', value: 'all'},
      {title: 'В управлении', value: 'controlled'},
      {title: 'Не в управлении', value: 'uncontrolled'},
    ])
    const users = ref([]);
    const loading = ref(false);
    const {
      isLoading: isUsersLoading,
      data: usersData,
      error: usersError,
      executeApiCall: getUsersAction,
    } = useApiCall(loadUsers, true)
    const {
      isLoading: isUserLoading,
      data: userData,
      error: userError,
      executeApiCall: getUserAction,
    } = useApiCall(loadUser, true)

    const filter = ref('')

    watch(() => filter.value, async () => {
      await filterUser(filter.value)
    })

    const filterUser = async (value) => {
      loading.value = true;
      if (value) {
        await getUsersAction({page: 1, search: value})
        if (usersData.value) {
          const formatArray = []
          usersData.value.data.data.forEach(user => {
            formatArray.push({
              label: `${user.last_name ?? '-'} ${user.first_name ?? '-'} ${user.patronymic ?? '-'}`,
              value: user.id,
              phone: `${user.phone_code ? user.phone_code : ''}${user.phone}`,
            })
          })
          users.value = [...formatArray]
        }
      } else {
        users.value = []
      }
      loading.value = false;
    }
    const changeRole = (item) => {
      if (item.value !== -1) {
        formCreateNotice.value.user_id = null
        filter.value = ''
      }
    }

    const visibleModal = ref(false);

    const changeConfirmationStateModal = () => {
      visibleModal.value = !visibleModal.value
    }
    const isLoading = ref(true);
    onBeforeMount(async () => {
      try {
        const {loadNotice, loadTypeNotices} = useNotices();
        const {loadRoles, loadAppRoles} = useRoles();
        await loadRoles();
        await loadAppRoles();
        await loadNotice(route.params.id);
        await loadTypeNotices();
        formCreateNotice.value  = {...notice.value};
        if (notice.value.roles.length) {
          formCreateNotice.value.role = notice.value.roles.length > 1 ? -2 : notice.value.user_id ? -1 : notice.value.roles[0].id;
        } else if (notice.value.user_id) {
          await getUserAction(notice.value.user_id)
          if (userData.value) {
            users.value = [{
                label: `${userData.value.data.last_name ?? '-'} ${userData.value.data.first_name ?? '-'} ${userData.value.data.patronymic ?? '-'}`,
                value: userData.value.data.id,
                phone: `${userData.value.data.phone_code ? userData.value.data.phone_code : ''}${userData.value.data.phone}`,
              }
            ]
          }
          formCreateNotice.value.user_id = users.value.length ? users.value[0] : null
          formCreateNotice.value.role = -1
        }
        formCreateNotice.value.push_type_id = formCreateNotice.value.type.id;
        formCreateNotice.value.send_now = 0;
        breadcrumbs.value = [
          {label: 'Уведомления', router: {name: 'notices'}},
          {label: notice.value.id}
        ];
      } catch (e) {
        console.error(e);
      }
      isLoading.value = false;
    })

    return {
      t,
      toUpdateNotice,
      toDeclineNotice,
      breadcrumbs,
      apiRoles,
      typeNotices,
      notice,
      formCreateNotice,
      v$,
      textField,
      visibleConfirmationModal,
      changeVisible,
      noticeTypeForm,
      createTypeNotice,
      allRoles,
      isOwnerId,
      statuses,
      users,
      loading,
      filter,
      errors,
      filterUser,
      route,
      isLoading,
      changeRole,
      visibleModal,
      changeConfirmationStateModal,
      titleField,
    };
  }
});
</script>
<style lang="css" scoped>
.spinner__wrapper {
  display: flex;
  justify-content: center;
  align-items: center;
  height: 100%;
  min-height: 500px;
}
::v-deep(.p-progress-spinner-circle) {
  stroke: var(--color-primary) !important;
}
.calendar-icon {
  position: absolute;
  right: 15px;
  top: 25px;
  pointer-events: none;
}
.current-option {
  display: flex;
  flex-direction: column;
}
.current-option__phone {
  font-weight: 400;
  font-size: 11px;
  line-height: 18px;
  color: #666666;
}
.current-option__label {
  font-weight: 400;
  font-size: 13px;
  line-height: 22px;
  vertical-align: middle;
  color: #333333;
}
.notice__wrapper {
  overflow: hidden;
}
.nowrap_label {
  overflow: hidden;
  text-overflow: ellipsis;
  width: calc(100% - 40px) !important;
  white-space: nowrap !important;
  padding-right: 24px !important;
}
.notice__header {
  display: flex;
  flex-direction: column;
  gap: 16px;
  @media screen and (min-width: 1280px){
    flex-direction: row;
  }
}
</style>
<template>
  <ConfirmationModal v-model:visible="visibleModal">
    <template #header>
      Удалить уведомление
    </template>

    <template #default>
      <span class="flex mt-2">Вы уверены, что хотите удалить уведомление?</span>
    </template>

    <template #footer>
      <div class="flex justify-content-between mt-4">
        <Button :label="t('labels.cancel')" @click="changeConfirmationStateModal"
                class="btn-primary-outlined font-light w-12"/>
        <Button :label="t('labels.destroy')" @click="toDeclineNotice"
                class="btn-primary font-light ml-3 w-12"/>
      </div>
    </template>
  </ConfirmationModal>
  <ConfirmationModal :visible="visibleConfirmationModal">
    <template #header>
      Создание типа уведомления
    </template>

    <template #default>
      <span class="p-float-label w-full">
        <InputText v-model="noticeTypeForm.name" id="name" class="w-full"/>
        <label for="name">Название типа</label>
      </span>
    </template>

    <template #footer>
      <div class="flex">
        <Button @click="changeVisible" label="Отмена" class="btn-error-outlined font-light"/>
        <Button @click="createTypeNotice" label="Создать тип" class="btn-primary font-light ml-3"/>
      </div>
    </template>
  </ConfirmationModal>
  <div v-if="isLoading" class="spinner__wrapper">
    <ProgressSpinner />
  </div>
  <template v-else>
    <section class="py-2 mb-3">
      <div class="flex justify-content-between">
        <div class="flex align-items-center">
          <Breadcrumb :data="breadcrumbs" separator="/"/>
          <span class="lowercase text-3xl" v-if="notice?.type">&nbsp;({{ notice.type.name }})</span>

          <Badge
              v-if="notice.state.code === 'draft'"
              class="ml-2 btn-black-20 border-round-2xl"
              size="large"
          >Черновик</Badge>

          <Badge
              v-if="notice.state.code === 'pending'"
              class="ml-2 border-round-2xl"
              size="large"
          >Ожидает отправки</Badge>

          <Badge
              v-if="notice.state.code === 'sent'"
              class="ml-2 btn-success border-round-2xl"
              size="large"
          >Отправлено</Badge>
        </div>

        <div class="flex notice__header">
          <Button :label="notice?.state?.code === 'draft' ? 'Удалить черновик' : 'Удалить'" @click="changeConfirmationStateModal"
                  class="btn-error-outlined font-light"/>
          <Button
              v-if="notice?.state?.code === 'draft'"
              @click="toUpdateNotice"
              label="Запланировать отправку"
              class="btn-primary font-light"
          />
        </div>
      </div>
    </section>

    <section class="py-2 mb-3">
      <div class="grid">
        <div class="col-12">
          <MainCard title="Наименование уведомления">
          <span class="p-float-label w-full">
            <InputText
                v-model="formCreateNotice[titleField]"
                id="name"
                class="w-full"
                :class="{'p-invalid': v$.title.$errors.length}"
            />
            <label for="name">Наименование</label>
          </span>
            <span v-if=" v$[titleField].$errors.length" class="text-xs color-error">
            {{  v$[titleField].$errors[0].$message }}
          </span>
          </MainCard>
        </div>
        <div class="col-12 xl:col-4 md:col-6">
          <MainCard title="Выберите или создайте тип уведомления">
            <div class="w-full mb-2">
              <Dropdown
                  v-model="formCreateNotice.push_type_id"
                  :class="{'p-invalid': v$.push_type_id.$errors.length}"
                  :options="typeNotices"
                  optionLabel="name"
                  option-value="id"
                  placeholder="Тип уведомления"
                  class="w-full"/>
              <span v-if="v$.push_type_id.$errors.length" class="text-xs color-error">
              {{ v$.push_type_id.$errors[0].$message }}
            </span>
            </div>

            <span @click="changeVisible" class="color-primary underline cursor-pointer">
            Создать новый тип уведомления
          </span>
          </MainCard>
        </div>

        <div class="col-12 xl:col-4 md:col-6">
          <MainCard title="Дата и время отправки">
            <div class="grid">
              <div class="col-12 md:col-6 relative">
                <Calendar
                    v-model="formCreateNotice.send_date"
                    :disabled="!!formCreateNotice.send_now"
                    date-format="d.m.yy"
                    placeholder="Дата"
                    class="w-full"
                />
                <img src="@/assets/images/icons/calendar.svg" class="calendar-icon" alt="" />
                <span v-if="errors.send_date" class="text-xs color-error">
                {{ errors.send_date[0] }}
              </span>
              </div>
              <div class="col-12 md:col-6">
                <div class="w-full">
                <span class="p-input-icon-left p-float-label w-full">
                  <i class="pi pi-search"/>
                  <InputMask
                      id="time"
                      v-model="formCreateNotice.send_time"
                      :disabled="!!formCreateNotice.send_now"
                      class="w-full"
                      mask="99:99"
                      placeholder="12:00"
                  />
                  <label for="time">Время</label>
                </span>
                  <span v-if="errors.send_time" class="text-xs color-error">
                  {{ errors.send_time[0] }}
                </span>
                </div>
              </div>
            </div>

            <Checkbox v-model="formCreateNotice.send_now" :binary="true" name="send_now" id="send_now"/>
            <label for="send_now" class="ml-1">Отправить немедленно</label>
          </MainCard>
        </div>

        <div class="col-12 xl:col-4 md:col-6">
          <MainCard title="Срок действия данных">
            <div class="grid">
              <div class="col-12 relative">
                <Calendar
                    v-model="formCreateNotice.expiration_date"
                    :class="{'p-invalid': errors.expiration_date}"
                    date-format="d.m.yy"
                    placeholder="Дата окончания"
                    class="w-full"
                />
                <img src="@/assets/images/icons/calendar.svg" class="calendar-icon" alt="" />
                <span v-if="errors.expiration_date" class="text-xs color-error">
                {{ errors.expiration_date[0] }}
              </span>
              </div>
            </div>
          </MainCard>
        </div>
      </div>
    </section>

    <section class="py-2 mb-3">
      <div class="grid">
        <div class="col-12 xl:col-4 md:col-6">
          <MainCard title="Получатели уведомления">
            <div class="w-full mb-2">
              <Dropdown
                  v-model="formCreateNotice.role"
                  :class="{'p-invalid': v$.role.$errors.length || errors?.role}"
                  :options="allRoles"
                  optionLabel="name_ru"
                  option-value="id"
                  placeholder="Выберите получателей"
                  class="w-full"
                  @change="changeRole"
              />
              <span v-if="v$.role.$errors.length || errors?.role" class="text-xs color-error">
              {{ errors?.role ? errors.push_type_id[0] : v$.role.$errors[0].$message }}
            </span>
            </div>
          </MainCard>
        </div>

        <div class="col-12 xl:col-4 md:col-6">
          <MainCard v-if="isOwnerId && formCreateNotice.role === isOwnerId" title="Статус собственника">
            <div class="w-full mb-2">
              <Dropdown
                  v-model="formCreateNotice.control_status"
                  :class="{'p-invalid': v$.control_status.$errors.length || errors?.control_status}"
                  :options="statuses"
                  optionLabel="title"
                  option-value="value"
                  placeholder="Статус собственника"
                  class="w-full"/>
              <span v-if="v$.control_status.$errors.length || errors?.control_status" class="text-xs color-error">
              {{ errors?.control_status ? errors.control_status[0] : v$.control_status.$errors[0].$message }}
            </span>
            </div>
          </MainCard>
          <MainCard v-if="formCreateNotice.role === -1" title="Найти пользователя">
            <div class="w-full p-float-label mb-2 notice__wrapper">
              <Dropdown
                  id="users"
                  v-model="formCreateNotice.user_id"
                  :class="{'p-invalid': v$.user_id.$errors.length || errors?.user_id}"
                  :options="users"
                  optionLabel="label"
                  placeholder="Введите ФИО или номер телефона"
                  class="w-full"
                  @filter="filterUser"
              >
                <template #value="slotProps">
                  <div v-if="slotProps.value" class="current-option">
                    <span class="current-option__label">{{slotProps.value.label}}</span>
                    <span class="current-option__phone">{{slotProps.value.phone}}</span>
                  </div>
                  <div v-else class="flex" :style="{'min-height': '19px'}"></div>
                </template>
                <template #header>
                  <div class="flex relative p-input-icon-right">
                    <InputText
                        v-model="filter"
                        class="flex mx-2 my-2 w-full"
                    />
                    <i class="pi pi-search" style="right: 20px"/>
                  </div>
                </template>
                <template #empty>
                  <span v-if="loading">Загрузка...</span>
                  <span v-else>Пользователи не найдены</span>
                </template>
              </Dropdown>
              <label for="users" class="nowrap_label">Введите ФИО или номер телефона</label>
            </div>
            <span v-if="v$.user_id.$errors.length || errors?.user_id" class="text-xs color-error">
              {{ errors?.user_id ? errors.control_status[0] : v$.user_id.$errors[0].$message }}
            </span>
          </MainCard>
        </div>
      </div>
    </section>

    <section class="py-2 mb-3">
      <div class="grid">
        <div class="col-12">
          <MainCard title="Текст уведомления">
            <Editor v-model="formCreateNotice[textField]" class="w-full"/>
          </MainCard>
          <span class="text-xs color-error" v-if="v$[textField]?.$errors?.length">
            {{ v$[textField]?.$errors?.[0].$message }}
          </span>
        </div>
      </div>
    </section>
  </template>
</template>